import { LoanRequestDetailsComponent } from 'src/app/LoanRequest/loan-request-details/loan-request-details.component';
import { ConfirmationDialogComponent } from 'src/app/Shared/confirmation-dialog/confirmation-dialog.component';
import { Enums } from 'src/app/Shared/enums';
import { SharedService } from 'src/app/Shared/shared-service.service';
import { CompanyAccess } from 'src/model/companyAccess.model';
import { CustomerAccount } from 'src/model/customerAccount.model';
import { LoanRequest } from 'src/model/loanRequest.model';
import { LoanGroupRequest } from 'src/model/sharedLoan.model';
import { AccountAuthorizationService } from 'src/services/accountAuthorization.service';
import { AdminAccessService } from 'src/services/admin-access.service';
import { CustomerAccountService } from 'src/services/customer-account.service';
import { LoanRequestService } from 'src/services/loan-request.service';

import { Component, OnInit, inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';

import { AccountAuthorization } from 'src/model/accountAuthorization';
import { DialogItem } from '../../dialog-item';

@Component({
    selector: 'app-bookmark',
    templateUrl: './bookmark.component.html',
    styleUrls: ['./bookmark.component.scss'],
    standalone: false
})
export class BookmarkComponent implements OnInit {
  protected sharedService = inject(SharedService);
  private enums = inject(Enums);
  private _snackBar = inject(MatSnackBar);
  private acctAuthService = inject(AccountAuthorizationService);
  private accountService = inject(CustomerAccountService);
  private adminAccess = inject(AdminAccessService);
  private loanRequestService = inject(LoanRequestService);

  bookmarkStorage = 'bookmarks';

  bookmarks: number[] = [];
  bookmarkedList: LoanRequest[] = [];
  bookmarkedGroupList: LoanGroupRequest[] = [];
  filteredBookmarkedGroupList: LoanGroupRequest[] = [];
  deletedTickets: number[] = [];
  filteredDeletedTickets: number[] = [];
  searchText = "";
  searching = false;

  companyAccess: Record<string, CompanyAccess[]> = {};
  approvedAccountList: Record<string, CustomerAccount[]> = {};
  pendingAccountList: Record<string, CustomerAccount[]> = {};

  ngOnInit() {
    this.bookmarks = (localStorage.bookmarks != undefined) ? JSON.parse(localStorage.bookmarks).filter(x => x !== null) : [];
    this.refresh();
    this.getUserCompanyAccess();
  }

  async applyFilter() {
    if (!this.isSearchTextEmpty()) {
      try {
        this.resetFilter();
        this.filteredBookmarkedGroupList = this.filteredBookmarkedGroupList.filter(g => g.requestList.some(b => b.ID === parseInt(this.searchText, 10)));
        this.filteredDeletedTickets = this.deletedTickets.filter(
          (r) => r === parseInt(this.searchText, 10)
        );
        this.searching = true;
      } catch (error) {
        console.error(error);
      }
    } else {
      this.resetFilter();
    }
  }

  resetFilter() {
    this.searching = false;
    this.filteredBookmarkedGroupList = this.bookmarkedGroupList;
    this.filteredDeletedTickets = this.deletedTickets;
  }

  showMessage(message: string) {
    this._snackBar.open(message, '', {
      duration: 3000,
      verticalPosition: 'top',
      panelClass: ['top-snackbar', 'bg-light', 'text-dark']
    });
  }

  isSearchTextEmpty(): boolean {
    const result = this.searchText.length == 0;
    if (result) {
      this.showMessage('Please insert ticket number.');
    }
    return result;
  }

  async addBookmark(confirm = true) {
    if (!this.isSearchTextEmpty()) {
      const ticketId = parseInt(this.searchText, 10);

      if (!this.bookmarks.includes(ticketId)) {
        try {
          let id: number | number[];
          let t: LoanRequest | LoanRequest[] = await this.loanRequestService.getLoanApprovalRequestById(ticketId);
          if (t.LoanGroupId !== undefined) {
            t = await this.loanRequestService.getLoanGroupApprovalRequests(t.LoanGroupId);
            id = t.map(x => x.ID);
          } else id = t.ID;
          if (confirm) this.openConfirmationModal(t);
          else this.saveBookmark(id);
        } catch (error) {
          if (error.status == 404)
            this.showMessage('Ticket not found.')
        }
      } else {
        this.showMessage('Ticket already bookmarked.');
      }
    }
  }

  saveBookmark(id: number | number[]) {
    this.bookmarks = this.bookmarks.concat(id);
    localStorage.setItem(this.bookmarkStorage, JSON.stringify(this.bookmarks));
    this.refresh();
  }

  removeBookmark(id: number | number[]) {
    if (Array.isArray(id)) {
      const l = new Set<number>(id);
      this.bookmarks = this.bookmarks.filter((r) => !l.has(r))
    } else this.bookmarks = this.bookmarks.filter((r) => r !== id)
    localStorage.setItem(this.bookmarkStorage, JSON.stringify(this.bookmarks));
    this.searchText = '';
    this.refresh();
  }

  openConfirmationModal(req: LoanRequest | LoanRequest[]) {
    const modalRef = this.sharedService.openModal(ConfirmationDialogComponent, 'largeModal');
    const i = modalRef.componentInstance as ConfirmationDialogComponent;
    req = Array.isArray(req) ? req : [req];
    const dialogItems = req.map(r => new DialogItem(LoanRequestDetailsComponent, { request: r, viewOnly: true, onlyViewLoan: false, isExpanded: true }));
    i.injectContent = dialogItems;
    i.message = 'Are you sure to bookmark this loan request?';
    modalRef.result.then((confirm) => {
      if (confirm) {
        if (Array.isArray(req)) {
          this.saveBookmark(req.map(r => r.ID))
        }
      }
    });
  }

  get approvedBookmarks() {
    return this.filteredBookmarkedGroupList.filter(g => g.requestList.every(b => b.Approved === true));
  }

  get otherBookmarks() {
    return this.filteredBookmarkedGroupList.filter(g => !g.requestList.every(b => b.Approved === true));
  }

  async processAccountAuthorizations(authorizations: AccountAuthorization[]) {

    const approvedCustAcc = authorizations.filter(a => a.RevokedOn == undefined && (a.GrantedUntil != undefined && (this.sharedService.stripTime(new Date(a.GrantedUntil)) >= this.sharedService.stripTime(new Date())))).map(a => a.CustomerAccountId);
    const pendingCustAcc = authorizations.filter(a => a.RevokedOn == undefined && a.GrantedUntil == undefined).map(a => a.CustomerAccountId);

    if (approvedCustAcc.length > 0) {
      const accounts = await this.accountService.getAccountsDetails(approvedCustAcc);
      this.approvedAccountList = this.sharedService.groupByNumKey(accounts, 'CustomerId');
    }

    if (pendingCustAcc.length > 0) {
      const accounts = await this.accountService.getAccountsDetails(pendingCustAcc);
      this.pendingAccountList = this.sharedService.groupByNumKey(accounts, 'CustomerId');
    }

  }

  async refresh() {
    try {
      if (this.bookmarks.length > 0) {
        this.bookmarkedList = await this.loanRequestService.getLoanApprovalRequestByIds(this.bookmarks);
        const g = this.sharedService.groupByNumKeyAsMap(this.bookmarkedList, 'LoanGroupId');
        this.bookmarkedGroupList = Array.from(g, ([key, value]) => ({ loanGroup: key, requestList: value }));
        this.filteredBookmarkedGroupList = Object.assign(this.bookmarkedGroupList);

        const customerIds = this.bookmarkedList.map(request => request.CustomerId);
        const authorizations = await this.acctAuthService.getAllAccountAuthorizations(customerIds, undefined, undefined);
        // var custAcc = authorizations.filter(a => a.RevokedOn == undefined && (a.GrantedUntil != undefined && (this.sharedService.stripTime(new Date(a.GrantedUntil)) >= this.sharedService.stripTime(new Date())))).map(a => a.CustomerAccountId);
        this.processAccountAuthorizations(authorizations);

        this.deletedTickets = this.ticketNotExists();
      } else {
        this.bookmarkedList = [];
        this.bookmarkedGroupList = [];
        this.deletedTickets = [];
      }
      this.resetFilter();
    } catch (error) {
      console.error(error);
    }
  }

  ticketNotExists(): number[] {
    const list = new Set(this.bookmarkedList.map(x => x.ID));
    var result: number[] = result = this.bookmarks.reduce((a, t) => {
      if (!list.has(t)) a.push(t);
      return a;
    }, []);
    return result;
  }

  async getUserCompanyAccess() {
    const priorities: number[] = [this.enums.FIRST_PRIORITY, this.enums.SECOND_PRIORITY, this.enums.THIRD_PRIORITY];
    const a = await this.adminAccess.getCompaniesAccess();
    const r = this.sharedService.groupBy(a, 'CompanyId');
    Object.keys(r).forEach(key => {
      this.companyAccess[key] = r[key].filter(a => priorities.includes(a.Priority));
      this.companyAccess[key].sort((a, b) => a.Priority - b.Priority);
    });
  }
}
